
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// API エンドポイント
const FUNCTIONS_URL = `${supabaseUrl}/functions/v1`

export const apiClient = {
  // ブログ API
  blogs: {
    getAll: async () => {
      const response = await fetch(`${FUNCTIONS_URL}/blog-api`)
      if (!response.ok) throw new Error('Failed to fetch blogs')
      return response.json()
    },
    create: async (blog: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/blog-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blog)
      })
      if (!response.ok) throw new Error('Failed to create blog')
      return response.json()
    },
    update: async (blog: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/blog-api`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blog)
      })
      if (!response.ok) throw new Error('Failed to update blog')
      return response.json()
    },
    delete: async (id: number) => {
      const response = await fetch(`${FUNCTIONS_URL}/blog-api`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      if (!response.ok) throw new Error('Failed to delete blog')
      return response.json()
    }
  },

  // お知らせ API
  announcements: {
    getActive: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      
      if (error && error.code !== 'PGRST116') throw error
      return data
    },
    getAll: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data
    },
    create: async (message: string) => {
      const { data, error } = await supabase
        .from('announcements')
        .insert([{ message, is_active: true }])
        
      if (error) throw error
      return data
    },
    update: async (id: number, message: string, is_active: boolean = true) => {
      const { data, error } = await supabase
        .from('announcements')
        .update({ message, is_active, updated_at: new Date().toISOString() })
        .eq('id', id)
        
      if (error) throw error
      return data
    },
    delete: async (id: number) => {
      const { data, error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id)
        
      if (error) throw error
      return data
    }
  },

  // リンク API
  links: {
    getAll: async () => {
      const response = await fetch(`${FUNCTIONS_URL}/links-api`)
      if (!response.ok) throw new Error('Failed to fetch links')
      return response.json()
    },
    create: async (link: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/links-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(link)
      })
      if (!response.ok) throw new Error('Failed to create link')
      return response.json()
    },
    update: async (link: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/links-api`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(link)
      })
      if (!response.ok) throw new Error('Failed to update link')
      return response.json()
    },
    delete: async (id: number) => {
      const response = await fetch(`${FUNCTIONS_URL}/links-api`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      if (!response.ok) throw new Error('Failed to delete link')
      return response.json()
    }
  },

  // 検証データ API
  verification: {
    getAll: async (type?: string) => {
      const url = type 
        ? `${FUNCTIONS_URL}/verification-api?type=${type}`
        : `${FUNCTIONS_URL}/verification-api`
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch verification data')
      return response.json()
    },
    create: async (data: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/verification-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to create verification data')
      return response.json()
    },
    update: async (data: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/verification-api`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to update verification data')
      return response.json()
    },
    delete: async (id: string) => {
      const response = await fetch(`${FUNCTIONS_URL}/verification-api`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      if (!response.ok) throw new Error('Failed to delete verification data')
      return response.json()
    }
  },

  // 掲示板 API
  forum: {
    // 投稿一覧取得
    getPosts: async () => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api`)
      if (!response.ok) throw new Error('Failed to fetch posts')
      return response.json()
    },
    // 投稿詳細取得
    getPost: async (id: number) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/${id}`)
      if (!response.ok) throw new Error('Failed to fetch post')
      return response.json()
    },
    // 新規投稿作成
    createPost: async (post: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(post)
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create post')
      }
      return response.json()
    },
    // 返信作成
    createReply: async (reply: any) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reply)
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create reply')
      }
      return response.json()
    },
    // 投稿削除（管理者用）
    deletePost: async (id: number) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/posts`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      if (!response.ok) throw new Error('Failed to delete post')
      return response.json()
    },
    // 返信削除（管理者用）
    deleteReply: async (id: number) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/replies`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      if (!response.ok) throw new Error('Failed to delete reply')
      return response.json()
    },
    // NGワード取得
    getNGWords: async () => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/ng-words`)
      if (!response.ok) throw new Error('Failed to fetch NG words')
      return response.json()
    },
    // NGワード追加
    addNGWord: async (word: string) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/ng-words`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word })
      })
      if (!response.ok) throw new Error('Failed to add NG word')
      return response.json()
    },
    // NGワード削除
    deleteNGWord: async (id: number) => {
      const response = await fetch(`${FUNCTIONS_URL}/forum-api/ng-words`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      if (!response.ok) throw new Error('Failed to delete NG word')
      return response.json()
    }
  }
}
